import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  useBranches,
  useCreateBranch,
  usePatchBranch,
} from '../hooks/useBranches.js'
import { OwnerApiError } from '../ownerApi.js'
import { BranchMapPicker } from '../branches/BranchMapPicker.js'
import type { OwnerBranch } from '../types.js'

/**
 * Slice 4b — owner-facing branches list. Surfaces every active branch with
 * its name, slug, and lat/lng. Selecting one opens an inline editor with a
 * draggable map pin (BranchMapPicker, lazy-imported so the Leaflet bundle
 * only loads when the owner reaches this page).
 *
 * Coords-only edits trigger a fire-and-forget wallet sync on the backend
 * (syncTenantBranchLocations) — see CLAUDE.md "Richer wallet pass v2".
 * Renaming a branch is also handled here (the prior shared /branches/:id
 * PATCH route handles both in one shape).
 */
export function BranchesPage() {
  const branchesQ = useBranches()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  if (branchesQ.isLoading) return <p className="owner-loading">Loading…</p>
  if (branchesQ.error) {
    return (
      <p className="owner-error">
        Couldn't load branches. Try refreshing.
      </p>
    )
  }
  const branches = branchesQ.data?.branches ?? []
  const active = branches.filter((b) => b.archived_at === null)
  // Branch-seat cap, set by the admin (tenants.max_branches). The GET reads
  // it live, so an admin raising it shows up here on the next list fetch.
  const cap = branchesQ.data?.cap ?? { count: active.length, cap: active.length }
  const atCap = cap.count >= cap.cap
  const remaining = Math.max(0, cap.cap - cap.count)

  return (
    <div>
      <h1>Branches</h1>
      <p className="owner-greeting-sub">
        Each branch can have a map pin. Customers near a branch with a pin
        will see their loyalty pass appear on their phone's lock screen
        automatically.
      </p>

      <div className="owner-card">
        <div className="owner-card-title">
          Branches: {cap.count} of {cap.cap} used
        </div>
        <p className="owner-card-sub">
          {atCap
            ? 'You’ve used every branch slot. Contact Naya to raise your limit.'
            : `You can add ${remaining} more branch${
                remaining === 1 ? '' : 'es'
              }.`}
        </p>
        {!adding && (
          <button
            type="button"
            className="owner-btn owner-btn-primary owner-btn-sm"
            style={{ marginTop: 10 }}
            disabled={atCap}
            onClick={() => setAdding(true)}
          >
            Add branch
          </button>
        )}
      </div>

      {adding && <AddBranchCard onClose={() => setAdding(false)} />}

      {active.length === 0 ? (
        <div className="owner-card">
          <p>No branches yet. Add your first one above.</p>
        </div>
      ) : (
        <div className="owner-branches-list">
          {active.map((branch) => (
            <BranchCard
              key={branch.id}
              branch={branch}
              isEditing={editingId === branch.id}
              onEdit={() => setEditingId(branch.id)}
              onClose={() => setEditingId(null)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AddBranchCard({ onClose }: { onClose: () => void }) {
  const create = useCreateBranch()
  const [name, setName] = useState('')

  async function submit() {
    const trimmed = name.trim()
    if (trimmed.length === 0) {
      toast.error('Enter a branch name')
      return
    }
    try {
      await create.mutateAsync({ name: trimmed })
      toast.success('Branch added')
      onClose()
    } catch (err) {
      const msg =
        err instanceof OwnerApiError ? err.userMessage : 'Could not add branch'
      toast.error(msg)
    }
  }

  return (
    <div className="owner-card">
      <div className="owner-card-title">Add branch</div>
      <div className="owner-field" style={{ marginTop: 10 }}>
        <label className="owner-label" htmlFor="new-branch-name">
          Branch name
        </label>
        <input
          id="new-branch-name"
          name="branch-name"
          autoComplete="off"
          className="owner-input"
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Keopi Cup - SM City"
          autoFocus
        />
      </div>
      <div className="owner-form-actions" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="owner-btn"
          onClick={onClose}
          disabled={create.isPending}
        >
          Cancel
        </button>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={submit}
          disabled={create.isPending}
        >
          {create.isPending ? 'Adding…' : 'Add branch'}
        </button>
      </div>
    </div>
  )
}

interface BranchCardProps {
  branch: OwnerBranch
  isEditing: boolean
  onEdit: () => void
  onClose: () => void
}

function BranchCard({ branch, isEditing, onEdit, onClose }: BranchCardProps) {
  const patch = usePatchBranch()
  const [name, setName] = useState(branch.name)
  // numeric() comes back as a string from the API; coerce to number for the
  // map. Empty when null = no pin set.
  const initialLat =
    branch.latitude !== null ? Number(branch.latitude) : null
  const initialLng =
    branch.longitude !== null ? Number(branch.longitude) : null
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    initialLat !== null && initialLng !== null
      ? { lat: initialLat, lng: initialLng }
      : null,
  )

  // Reset local state when the editor opens against a different branch row
  // (e.g. after a successful save invalidates the list and the row re-renders
  // with new data).
  useEffect(() => {
    if (isEditing) {
      setName(branch.name)
      setCoords(
        initialLat !== null && initialLng !== null
          ? { lat: initialLat, lng: initialLng }
          : null,
      )
    }
    // We deliberately key on isEditing only — re-rendering when initialLat/Lng
    // shift mid-edit would clobber the owner's in-progress drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  const save = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Branch name required')
      return
    }
    try {
      await patch.mutateAsync({
        branchId: branch.id,
        input: {
          name: trimmedName !== branch.name ? trimmedName : undefined,
          latitude: coords ? coords.lat : null,
          longitude: coords ? coords.lng : null,
        },
      })
      toast.success('Branch saved')
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed'
      toast.error(msg)
    }
  }

  if (!isEditing) {
    return (
      <div className="owner-card owner-branch-row">
        <div>
          <div className="owner-card-title">{branch.name}</div>
          <p className="owner-card-sub">
            {coords
              ? `Pin set · ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`
              : 'No map pin yet'}
          </p>
        </div>
        <button type="button" className="owner-btn" onClick={onEdit}>
          Edit
        </button>
      </div>
    )
  }

  return (
    <div className="owner-card">
      <div className="owner-card-title">Edit branch</div>
      <label className="owner-field">
        <span className="owner-field-label">Branch name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          className="owner-input"
        />
      </label>

      <div className="owner-field">
        <span className="owner-field-label">Map pin (optional)</span>
        <p className="owner-card-sub">
          Drag the map so the pin sits on your branch. The pass will surface
          on customers' lock screens nearby.
        </p>
        <BranchMapPicker
          value={coords}
          onChange={setCoords}
        />
        {coords && (
          <button
            type="button"
            className="owner-btn"
            onClick={() => setCoords(null)}
            style={{ marginTop: '0.5rem' }}
          >
            Clear pin
          </button>
        )}
      </div>

      <div className="owner-form-actions">
        <button
          type="button"
          className="owner-btn"
          onClick={onClose}
          disabled={patch.isPending}
        >
          Cancel
        </button>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={save}
          disabled={patch.isPending}
        >
          {patch.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
